/*
 * Imports
 */

import { User } from "firebase/auth";

import { StepsAny } from "./productionSteps";
import { Timestamp } from "firebase/firestore";

/**
 * DB Collection Names
 */

export const DB_COLLECTION_NAMES = {
  MODULES: "modules",
  AUXILIARY_COMPONENTS: "auxiliary-components",
  STEPS: "steps",
  STORE: "store",
  USERS: "users",
} as const;
export type DbCollections =
  (typeof DB_COLLECTION_NAMES)[keyof typeof DB_COLLECTION_NAMES];

/*
 * Modules
 */

export const MODULE_VARIANTS = {
  MODULE_2S: "2S",
  MODULE_PS: "PS",
} as const;
export const MODULE_LOCATIONS = {
  BROWN: "Brown",
  RUTGERS: "Rutgers",
  PRINCETON: "Princeton",
  FERMILAB: "Fermilab",
  COURIER: "Courier",
} as const;
export const MODULE_LOCATIONS_FINAL = {
  FERMILAB: "Fermilab",
  LYON: "Lyon",
  IPHC: "IPHC",
} as const;
export const MODULE_STATUSES = {
  PRODUCTION: "production",
  SHIPPING: "shipping",
  COMPLETED: "completed",
  DO_NOT_USE: "do_not_use",
} as const;

export type PossibleLocations =
  (typeof MODULE_LOCATIONS)[keyof typeof MODULE_LOCATIONS];
export type FinalLocations =
  (typeof MODULE_LOCATIONS_FINAL)[keyof typeof MODULE_LOCATIONS_FINAL];
export type PossibleLocationsOrAll = PossibleLocations | "All";
type ModuleLocationStationary = {
  inTransit: false;
  location: PossibleLocations;
};
export type ModuleLocationInTransit = {
  inTransit: true;
  location: PossibleLocations;
  origin: PossibleLocations;
  destination: PossibleLocations;
};
export type ModuleLocation = ModuleLocationStationary | ModuleLocationInTransit;

export const MODULE_LOCATIONS_LIST: PossibleLocations[] = Object.values(
  MODULE_LOCATIONS,
).filter((loc) => loc !== MODULE_LOCATIONS.COURIER);
export const MODULE_LOCATIONS_FINAL_LIST: FinalLocations[] = Object.values(
  MODULE_LOCATIONS_FINAL,
);

export type ModuleVariant =
  (typeof MODULE_VARIANTS)[keyof typeof MODULE_VARIANTS];
export type ModuleStatus =
  (typeof MODULE_STATUSES)[keyof typeof MODULE_STATUSES];

export type ModuleImage = {
  src: string;
  comments: string;
  title: string;
  timestamp: Timestamp;
};

export type ModuleContributorDetails = {
  userId: string;
  userName: string;
};

export type ModuleContributors = {
  contributorIds: string[];
  contributorDetails: ModuleContributorDetails[];
};

export type Module = {
  id: string;
  location: ModuleLocation;
  status: ModuleStatus;
  serials: string[];
  steps: StepsAny[];
  images: ModuleImage[];
  contributors: ModuleContributors;
  lastUpdated?: Timestamp;
  createdOn?: Timestamp;
  queryTokens?: string[];
  tags?: string[];
  debugTags?: string[];
  moduleStatus: ModuleStatus;
};

export type ModuleWithUpNext = Module & {
  nextStep: string;
};

/*
 * Steps
 */

export type StepTypes = StepsAny;

export type StepDetails = {
  [key: string]: string | number | boolean | undefined;
};

export type BooleanString = boolean | "true" | "false";

export type StepImage = {
  src?: string;
  alt?: string;
};

export type StepBase = {
  id: string;
  stepName: StepTypes;
  userId: string;
  userName: string;
  moduleId: string;
  mainComponentSerial: string;
  details: StepDetails;
  isCernImport: boolean;
  passed?: BooleanString;
  comments: string;
  image?: StepImage;
};

export type StepDb = StepBase & { timestamp: Timestamp };
export type StepFrontend = StepBase & { timestamp: Date };
export type Step = StepDb | StepFrontend;

export function booleanStringToBoolean(booleanString: BooleanString): boolean {
  if (typeof booleanString === "boolean") return booleanString;
  if (typeof booleanString === "string") {
    if (booleanString === "true") return true;
    if (booleanString === "false") return false;
    throw new Error(`Invalid key for BooleanString ${booleanString}`);
  }
  throw new Error(`Invalid type for BooleanString ${booleanString}`);
}

/*
 * Users
 */

const USER_ROLES = ["admin", "user"] as const;
export type UserRoles = (typeof USER_ROLES)[number];

export type AuthUser = {
  firebaseUser?: User;
  firstName: string;
  lastName: string;
  email: string;
  location: PossibleLocations;
  role: UserRoles;
};

/**
 * Store
 */

const PART_TYPE_DEFINITIONS = {
  "2S": ["Module", "Sensor", "FEH", "SEH", "VTRX"],
  PS: ["Module", "Sensor", "POH", "ROH", "FEH", "MaPSA", "CFBaseplate", "VTRX"],
  Aux: {
    Fixture: [
      "2S KaptonGluing",
      "PS KaptonGluing",
      "2S HVTailWirebond",
      "PS HVTailWirebond",
      "2S ModuleWirebond",
      "PS ModuleWirebond",
      "SkeletonAssembly",
      "2S HVTailGluing",
      "PS HVTailGluing",
      "InsertGluing",
      "SensorGluing",
      "2S HybridGluing",
      "PS HybridGluing",
      "2S ModuleCarrier",
      "PS ModuleCarrier",
      "Metrology",
      "Encapsulation",
    ],
    Glue: ["EP_601_LV", "Araldite_2012", "TC_437"],
    Encapsulant: ["Slyguard_186"],
  },
} as const;

type PartTypeDefinitionsType = typeof PART_TYPE_DEFINITIONS;
type PartTypeKeys = keyof PartTypeDefinitionsType;

type PartTypeDefinitionsTypeAux = PartTypeDefinitionsType["Aux"];
type PartTypeAuxKeys = keyof PartTypeDefinitionsTypeAux;

type PartTypesHelper<
  PartT extends PartTypeKeys,
  SubT = undefined,
> = PartT extends "Aux"
  ? SubT extends PartTypeAuxKeys
    ? `${PartT}.${SubT}.${PartTypeDefinitionsTypeAux[SubT][number]}`
    : never
  : `${PartT}.${PartTypeDefinitionsType[Exclude<PartT, "Aux">][number]}`;

export type PartTypeAux = PartTypesHelper<"Aux", PartTypeAuxKeys>;
export type PartType2S = PartTypesHelper<"2S">;
export type PartTypePS = PartTypesHelper<"PS">;
export type PartType = PartTypeAux | PartType2S | PartTypePS;

export const PartTypeDefinitions2s = PART_TYPE_DEFINITIONS["2S"].map(
  (levelTwoType) => `2S.${levelTwoType}`,
) as PartType2S[];
export const PartTypeDefinitionsPs = PART_TYPE_DEFINITIONS["PS"].map(
  (levelTwoType) => `PS.${levelTwoType}`,
) as PartTypePS[];
export const PartTypeDefinitionsAux = [
  PART_TYPE_DEFINITIONS["Aux"]["Fixture"].map(
    (levelThreeType) => `Aux.Fixture.${levelThreeType}`,
  ),
  PART_TYPE_DEFINITIONS["Aux"]["Glue"].map(
    (levelThreeType) => `Aux.Glue.${levelThreeType}`,
  ),
  PART_TYPE_DEFINITIONS["Aux"]["Encapsulant"].map(
    (levelThreeType) => `Aux.Encapsulant.${levelThreeType}`,
  ),
].reduce((prev, next) => prev.concat(next), []) as PartTypeAux[];

export const PartTypeDefinitions = [
  PartTypeDefinitions2s,
  PartTypeDefinitionsPs,
  PartTypeDefinitionsAux,
].reduce((prev, next) => prev.concat(next), [] as PartType[]);

export function getHumanReadableAuxPartType(partType: PartTypeAux) {
  if (partType) {
    const type = partType.split(".")[1];
    const name = partType.split(".")[2];
    return `${name.split(/(?=[A-Z][a-z])/).join(" ")} (${type})`;
  } else {
    return "No part type";
  }
}

export const SERIAL_NUMBER_REGEXPS = {
  MODULE_2S: {
    MODULE: /^2S_\d{2}_\d{1}_(BRN|PRI|RUT|FER)-\d{5}$/,
    SENSOR: /^\d{5}_\d{3}_2-S_MAIN0$/,
    FEH: /^2SFEH\d{2}(L|R)-\d{9}$/,
    SEH: /^2SSEH-\d{9}$/,
    VTRX: /^VTRx\+_\d{2}.\d{1}cm_\d{11}$/,
  },
  MODULE_PS: {
    MODULE:
      /^PS_\d{2}_\d{2}_(BRN|PRI|RUT|FER)-\d{5}$|^PS_\d{2}_(BRN|PRI|RUT|FER)-\d{5}$/,
    SENSOR: /^\d{5}_\d{3}_PSS_MAIN(R|L)$/,
    POH: /^PSPOH-\d{9}$/,
    ROH: /^PSROH\d{2}-\d{9}$/,
    FEH: /^PSFEH\d{2}(L|R)-\d{9}$/,
    MAPSA: /^(QPT|HPK|AEM)_\d{5}_\d{3}(L|R)$/,
    CFBASEPLATE:
      /^[A-Za-z]\d-\d{2}-\d{6}-\d-[A-Za-z]\d{2}-(\d|\d{2})_(\d|\d{2})$/,
    VTRX: /^VTRx\+_\d{2}.\d{1}cm_\d{11}$/,
  },
};

export const BYPASS_REGEX = /.*/;

export const PART_TYPE_REGEX_MAP: { [key in PartType]?: RegExp } = {
  "2S.Module": SERIAL_NUMBER_REGEXPS.MODULE_2S.MODULE,
  "2S.Sensor": SERIAL_NUMBER_REGEXPS.MODULE_2S.SENSOR,
  "2S.SEH": SERIAL_NUMBER_REGEXPS.MODULE_2S.SEH,
  "2S.FEH": SERIAL_NUMBER_REGEXPS.MODULE_2S.FEH,
  "2S.VTRX": SERIAL_NUMBER_REGEXPS.MODULE_2S.VTRX,
  "PS.Module": SERIAL_NUMBER_REGEXPS.MODULE_PS.MODULE,
  "PS.Sensor": SERIAL_NUMBER_REGEXPS.MODULE_PS.SENSOR,
  "PS.POH": SERIAL_NUMBER_REGEXPS.MODULE_PS.POH,
  "PS.ROH": SERIAL_NUMBER_REGEXPS.MODULE_PS.ROH,
  "PS.FEH": SERIAL_NUMBER_REGEXPS.MODULE_PS.FEH,
  "PS.MaPSA": SERIAL_NUMBER_REGEXPS.MODULE_PS.MAPSA,
  "PS.CFBaseplate": SERIAL_NUMBER_REGEXPS.MODULE_PS.CFBASEPLATE,
  "PS.VTRX": SERIAL_NUMBER_REGEXPS.MODULE_PS.VTRX,
  // Todo: Determine schema for Aux Serials!
  "Aux.Fixture.2S KaptonGluing": BYPASS_REGEX,
  "Aux.Fixture.PS KaptonGluing": BYPASS_REGEX,
  "Aux.Fixture.2S HVTailGluing": BYPASS_REGEX,
  "Aux.Fixture.PS HVTailGluing": BYPASS_REGEX,
  "Aux.Fixture.InsertGluing": BYPASS_REGEX,
  "Aux.Fixture.2S HVTailWirebond": BYPASS_REGEX,
  "Aux.Fixture.PS HVTailWirebond": BYPASS_REGEX,
  "Aux.Fixture.2S ModuleWirebond": BYPASS_REGEX,
  "Aux.Fixture.PS ModuleWirebond": BYPASS_REGEX,
  "Aux.Fixture.SensorGluing": BYPASS_REGEX,
  "Aux.Fixture.2S ModuleCarrier": /^2S-(BRN|FNL|WSU)-\d{4}$/,
  "Aux.Fixture.PS ModuleCarrier": /^PS-(BRN|FNL|WSU)-\d{4}$/,
  "Aux.Fixture.Metrology": BYPASS_REGEX,
  "Aux.Fixture.SkeletonAssembly": BYPASS_REGEX,
  "Aux.Fixture.2S HybridGluing": BYPASS_REGEX,
  "Aux.Fixture.PS HybridGluing": BYPASS_REGEX,
  "Aux.Fixture.Encapsulation": BYPASS_REGEX,
  "Aux.Glue.EP_601_LV": BYPASS_REGEX,
  "Aux.Glue.TC_437": BYPASS_REGEX,
  "Aux.Glue.Araldite_2012": BYPASS_REGEX,
  "Aux.Encapsulant.Slyguard_186": BYPASS_REGEX,
} as const;

/*
const LOCATION_CODES = ['BRN', 'PRI', 'RUT', 'FER'] as const;
type LocationCode = typeof LOCATION_CODES[number];
const HYBRID_ORIENTATIONS = ['L', 'R'] as const;
type HybridOrientation = typeof HYBRID_ORIENTATIONS[number];
const MANUFACTURER_CODES = ['QPT', 'HPK', 'AEM'] as const;
type ManufacturerCode = typeof MANUFACTURER_CODES[number];
const LETTERS = [
    'A', 'B', 'C', 'D',
    'E', 'F', 'G', 'H',
    'I', 'J', 'K', 'L',
    'M', 'N', 'O', 'P',
    'Q', 'R', 'S', 'T',
    'U', 'V', 'W', 'X',
    'Y', 'Z',
] as const;
type LetterAThroughZ = typeof LETTERS[number] | Lowercase<typeof LETTERS[number]>;
*/
export type SerialSchema2sModule = string; // `2S_${D2}_${D1}_${LocationCode}-${D5}`;
export type SerialSchema2sSensor = string; // `${D5}_${D3}_2-S_MAIN0`;
export type SerialSchema2sFeh = string; // `2SFEH${D2}${HybridOrientation}-${D9}`;
export type SerialSchema2sSeh = string; // `2SSEH-${D9}`;

export type SerialSchemaPsModule = string; // `PS_${D2}_${D2}_${LocationCode}-${D5}|PS_${D2}_${LocationCode}-${D5}`;
export type SerialSchemaPsSensor = string; // `${D5}_${D3}_PSS_MAINR`;
export type SerialSchemaPsPoh = string; // `PSPOH-${D9}`;
export type SerialSchemaPsRoh = string; // `PSROH${D2}-${D9}`;
export type SerialSchemaPsFeh = string; // `PSFEH${D2}${HybridOrientation}-${D9}`;
export type SerialSchemaPsMapsa = string; // `${ManufacturerCode}_${D5}_${D3}${HybridOrientation}`;
export type SerialScehamCfBaseplate = string; // `${LetterAThroughZ}${D1}-${D2}-${D6}-${D1}-${LetterAThroughZ}${D2}_${D1 | D2}`;
export type SerialSchemaPsVTRX = string; // `${ManufacturerCode}_${D5}_${D3}${HybridOrientation}`;

export type SerialNumber =
  | SerialSchema2sModule
  | SerialSchema2sSensor
  | SerialSchema2sFeh
  | SerialSchema2sSeh
  | SerialSchemaPsModule
  | SerialSchemaPsSensor
  | SerialSchemaPsPoh
  | SerialSchemaPsRoh
  | SerialSchemaPsFeh
  | SerialSchemaPsMapsa
  | SerialScehamCfBaseplate
  | SerialSchemaPsVTRX
  | string;

//export type ModulesAtIncomplete = { [key in StepsAny]?: SerialNumber[] };
export type ModulesAtIncomplete = {
  [key in PossibleLocations]?: SerialNumber[];
};
export type ModulesAtStep = { [key in StepsAny]?: SerialNumber[] };
export type PartsByType = { [key in PartType]?: SerialNumber[] };
export type PartsAtLocation = { [key in PossibleLocations]?: SerialNumber[] };

export type StoreModuleStatus = {
  badModules: string[];
  modulesForStep: ModulesAtStep;
  incompleteModules: ModulesAtIncomplete;
  partsByType: PartsByType;
  partsByLocation: PartsAtLocation;
};

export type StoreHistory = {
  modules: string[];
  components: string[];
  "auxiliary-components": string[];
};

export type StoreTags = {
  tags: string[];
};

export type StoreDebugTags = {
  debugTags: string[];
};

/*
 * Auxiliary Components
 */

export const COMPONENT_CATEGORIES = ["fixture", "glue", "encapsulant"] as const;
export const COMPONENT_STATUSES = ["OKAY", "DO_NOT_USE", "IN_USE"] as const;

export type ComponentCategory = (typeof COMPONENT_CATEGORIES)[number];
export type ComponentStatus = (typeof COMPONENT_STATUSES)[number];

export type AuxiliaryComponent = {
  id: string;
  componentType: PartTypeAux;
  componentCategory: ComponentCategory;
  registeredOn: Timestamp;
  expirationDate?: Timestamp;
  comments?: string[];
  status: ComponentStatus;
  location: PossibleLocations;
  registeredBy: { authId: string; name: string };
};

/*
 * General
 */

export const ABSTRACT_COMPONENT_TYPES = [
  "Sensor",
  "Hybrid",
  "Module",
  "Baseplate",
  "Unknown",
] as const;
export const ABSTRACT_COMPONENT_SUBTYPE = [
  "Sensor",
  "MaPSA",
  "FEH",
  "SEH",
  "ROH",
  "POH",
  "VTRX",
  "None",
] as const;
export const COMPONENT_ORIENTATIONS = [
  "Left",
  "Right",
  "Top",
  "Bottom",
  "N/A",
] as const;

export type AbstractComponentType = (typeof ABSTRACT_COMPONENT_TYPES)[number];
export type AbstractComponentSubtype =
  (typeof ABSTRACT_COMPONENT_SUBTYPE)[number];
export type ComponentOrientation = (typeof COMPONENT_ORIENTATIONS)[number];

export type ComponentInfo = {
  serialNumber: string;
  shorthand: string;
  moduleType: ModuleVariant | "Unknown";
  componentType: AbstractComponentType;
  componentSubtype: AbstractComponentSubtype;
  orientation: ComponentOrientation;
  partTypeId: PartType | "Unknown";
};
